import React, { useEffect, useRef, useState } from 'react';
import { X, Eraser, Trash2 } from 'lucide-react';
import { clearSignatureCanvas, renderTypedSignature } from '../utils/typedSignature';

/**
 * Signature pad, modal variant of public-form/SignatureField.js with the same
 * two modes: Draw (pointer strokes) and Type (name rendered in a script face
 * via the shared typedSignature util).
 *
 * Two different "clears" live here, deliberately labelled apart: `Clear` wipes
 * the canvas mid-draw, while `Remove signature` empties the field back on the
 * page. Only the second appears, and only when that field already holds one. Both produce the same PNG data URL, so
 * nothing downstream can tell them apart. Kept separate from SignatureField
 * because the form field writes into a hidden input while this hands the data
 * URL back through a callback.
 */
export default function SignaturePadModal({ roleLabel, hasExisting, onRemove, onConfirm, onClose }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  // 'draw' | 'type' — same pair as SignatureField, same discard-on-switch rule.
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    // Backing size = CSS size (no devicePixelRatio scaling): the shared
    // renderTypedSignature draws in canvas coordinates, exactly like the
    // public form's pad.
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    return undefined;
  }, []);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    clearSignatureCanvas(canvasRef.current);
    setTypedName('');
    setHasInk(false);
  };

  /** Re-render the canvas from the typed name on every keystroke. */
  const handleTypedChange = (name) => {
    setTypedName(name);
    const url = renderTypedSignature(canvasRef.current, name, '#111827');
    setHasInk(!!url);
  };

  /** Switching mode discards the current signature — what's on screen is what applies. */
  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    clear();
  };

  const typing = mode === 'type';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
      <div className='bg-white rounded-xl shadow-xl w-full max-w-md'>
        <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
          <div className='text-sm font-semibold text-gray-900'>Sign as {roleLabel}</div>
          <button onClick={onClose} className='p-1 rounded-md text-gray-400 hover:bg-gray-100'>
            <X size={16} />
          </button>
        </div>

        <div className='p-4'>
          <div
            role='tablist'
            aria-label='Signature method'
            className='inline-flex overflow-hidden border border-gray-200 rounded-lg mb-3'
          >
            {[
              { key: 'draw', label: 'Draw' },
              { key: 'type', label: 'Type' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type='button'
                role='tab'
                aria-selected={mode === key}
                onClick={() => switchMode(key)}
                className={`px-3 py-1 text-sm font-medium transition-colors ${
                  mode === key ? 'text-white bg-blue-600' : 'text-gray-600 bg-white hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {typing && (
            <input
              type='text'
              value={typedName}
              onChange={(e) => handleTypedChange(e.target.value)}
              placeholder='Type your full name'
              aria-label='Type your full name to sign'
              autoComplete='name'
              autoFocus
              className='w-full mb-3 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500'
            />
          )}

          <canvas
            ref={canvasRef}
            className={`w-full border border-dashed border-gray-300 rounded-lg bg-gray-50 ${
              typing ? '' : 'cursor-crosshair'
            }`}
            style={{ height: 160, touchAction: 'none' }}
            // In type mode the canvas is a preview, not an input surface — a
            // stray drag would scribble over the rendered name and the next
            // keystroke would silently erase the scribble.
            onPointerDown={typing ? undefined : start}
            onPointerMove={typing ? undefined : move}
            onPointerUp={typing ? undefined : end}
            onPointerLeave={typing ? undefined : end}
          />
          <div className='flex items-center justify-between mt-1'>
            <p className='text-[11px] text-gray-400'>
              {typing ? 'Your name is drawn above as your signature' : 'Draw your signature above'}
            </p>
            <button onClick={clear} className='inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700'>
              <Eraser size={12} /> Clear
            </button>
          </div>
        </div>

        <div className='flex items-center gap-2 px-4 py-3 border-t border-gray-100'>
          {/*
            Removing the signature already ON THE PAGE — not the same act as
            Clear above, which only wipes the canvas you are drawing on. This is
            the size-independent way to empty a field: a small placement may have
            no room for an overlay control, but the pad always has room.
          */}
          {hasExisting && (
            <button
              onClick={onRemove}
              className='inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50'
            >
              <Trash2 size={14} />
              Remove signature
            </button>
          )}
          <span className='flex-1' />
          <button onClick={onClose} className='px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-50'>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(canvasRef.current.toDataURL('image/png'))}
            disabled={!hasInk}
            className='px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
          >
            Apply signature
          </button>
        </div>
      </div>
    </div>
  );
}
